import axios from "axios";

/**
 * Seasons where today is between registration start (inclusive) and end date (inclusive),
 * optionally scoped to one league. See SeasonViewSet.active_season.
 *
 * @param {number} [leagueId] - If set, passed as league_id query param
 * @returns {Promise<object[]>} Serialized seasons (may be empty)
 */
export async function fetchActiveSeasons(leagueId) {
    const params = {};
    if (leagueId != null && leagueId !== "") {
        params.league_id = leagueId;
    }
    const res = await axios.get("/api/leagues/seasons/active-season/", {
        params,
        withCredentials: true,
    });
    return Array.isArray(res.data) ? res.data : [];
}

/**
 * First active season for the optional league, or null if none.
 * Use with embedded league.seasons to prefer a matching full object when present.
 *
 * @param {number} [leagueId]
 * @returns {Promise<object|null>}
 */
export async function fetchDefaultActiveSeason(leagueId) {
    const list = await fetchActiveSeasons(leagueId);
    return list[0] ?? null;
}

/**
 * Resolve default season for UI: API active-season for this league, else first embedded season.
 *
 * @param {{ id: number, seasons?: object[] }} league
 * @returns {Promise<object|null>}
 */
export async function resolveDefaultSeasonForLeague(league) {
    if (!league) return null;
    const embedded = league.seasons || [];
    try {
        const apiSeason = await fetchDefaultActiveSeason(league.id);
        if (apiSeason) {
            const match = embedded.find((s) => s.id === apiSeason.id);
            return match ?? apiSeason;
        }
    } catch (e) {
        console.error("resolveDefaultSeasonForLeague:", e);
    }
    return embedded.length > 0 ? embedded[0] : null;
}

function parseSeasonDate(dateString) {
    if (!dateString) return null;
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? null : date;
}

function startOfToday() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
}

export function isPastSeason(season) {
    const endDate = parseSeasonDate(season?.end_date);
    if (!endDate) return false;

    const today = startOfToday();
    endDate.setHours(0, 0, 0, 0);
    return endDate < today;
}

/**
 * True when today falls in the season calendar window (start_date..end_date).
 */
export function isOngoingSeason(season) {
    const startDate = parseSeasonDate(season?.start_date);
    const endDate = parseSeasonDate(season?.end_date);
    if (!startDate || !endDate) return false;

    const today = startOfToday();
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return startDate <= today && endDate >= today;
}

/**
 * True when any of the caller's player IDs appears in season.participants.
 */
export function isUserRegisteredForSeason(season, playerIds = []) {
    if (!playerIds.length) return false;
    const participants = season?.participants;
    if (!Array.isArray(participants) || participants.length === 0) return false;

    const idSet = new Set(playerIds.map((id) => Number(id)));
    return participants.some((participant) => idSet.has(Number(participant.id)));
}

export function getSeasonOptionLabel(season) {
    const leagueName = season?.league_name ? ` (${season.league_name})` : "";
    const suffix = isPastSeason(season) ? " - Completed Season" : "";
    return `${season?.name || "Unnamed Season"}${leagueName}${suffix}`;
}

function compareSeasonsByRecency(left, right) {
    const leftDate = parseSeasonDate(left.start_date) || parseSeasonDate(left.end_date);
    const rightDate = parseSeasonDate(right.start_date) || parseSeasonDate(right.end_date);
    if (leftDate && rightDate) {
        return rightDate - leftDate;
    }
    return String(left.name || "").localeCompare(String(right.name || ""));
}

/**
 * Prefer currently ongoing seasons, then non-past, then most recent start.
 *
 * @param {object[]} seasons
 * @param {number[]} [playerIds] - When provided, only consider seasons the user is registered in
 * @returns {object|null}
 */
export function pickDefaultSeasonForUser(seasons = [], playerIds = []) {
    if (!seasons.length) return null;

    const hasParticipantData = seasons.some((season) => Array.isArray(season.participants));
    const registered = hasParticipantData
        ? seasons.filter((season) => isUserRegisteredForSeason(season, playerIds))
        : [];
    const pool = registered.length > 0 ? registered : seasons;

    const ongoing = pool.filter(isOngoingSeason);
    if (ongoing.length > 0) {
        return [...ongoing].sort(compareSeasonsByRecency)[0];
    }

    const nonPast = pool.filter((season) => !isPastSeason(season));
    const ranked = (nonPast.length > 0 ? nonPast : pool).slice().sort(compareSeasonsByRecency);
    return ranked[0] ?? null;
}

/**
 * @param {object[]} activeSeasons
 * @param {object[]} leagues
 * @param {{ playerIds?: number[], registeredOnly?: boolean }} [options]
 *   - playerIds + registeredOnly: keep only seasons the user is registered in
 *     (when participant data is present). Open-registration seasons the user
 *     has not joined are dropped so they cannot become the default.
 */
export function buildSeasonOptions(activeSeasons = [], leagues = [], options = {}) {
    const { playerIds = null, registeredOnly = false } = options;
    const byId = new Map();
    const activeIds = new Set(activeSeasons.map((season) => season.id));

    activeSeasons.forEach((season) => {
        if (season?.id != null) {
            byId.set(season.id, season);
        }
    });

    leagues.forEach((league) => {
        (league?.seasons || []).forEach((season) => {
            if (!season?.id) return;
            const existing = byId.get(season.id);
            byId.set(season.id, existing ? { ...season, ...existing } : season);
        });
    });

    let seasons = Array.from(byId.values());

    if (registeredOnly && playerIds?.length) {
        const hasParticipantData = seasons.some((season) => Array.isArray(season.participants));
        if (hasParticipantData) {
            const registered = seasons.filter((season) =>
                isUserRegisteredForSeason(season, playerIds)
            );
            if (registered.length > 0) {
                seasons = registered;
            }
        }
    }

    return seasons.sort((left, right) => {
        const leftOngoing = isOngoingSeason(left);
        const rightOngoing = isOngoingSeason(right);
        if (leftOngoing !== rightOngoing) {
            return leftOngoing ? -1 : 1;
        }

        const leftIsActive = activeIds.has(left.id);
        const rightIsActive = activeIds.has(right.id);
        if (leftIsActive !== rightIsActive) {
            return leftIsActive ? -1 : 1;
        }

        const leftIsPast = isPastSeason(left);
        const rightIsPast = isPastSeason(right);
        if (leftIsPast !== rightIsPast) {
            return leftIsPast ? 1 : -1;
        }

        return compareSeasonsByRecency(left, right);
    });
}
